const ENGAGED_KEY = 'carfido_pwa_engaged';
const DISMISSED_UNTIL_KEY = 'carfido_pwa_dismissed_until';
const INSTALLED_KEY = 'carfido_pwa_installed';
const STATE_EVENT = 'carfido_pwa_state_changed';
const DISMISSAL_MS = 30 * 24 * 60 * 60 * 1000;

let deferredInstallPrompt = null;
let initialized = false;

function readStorage(key) {
    try {
        return window.localStorage.getItem(key);
    } catch (_) {
        return null;
    }
}

function writeStorage(key, value) {
    try {
        window.localStorage.setItem(key, value);
    } catch (_) { /* Storage can be unavailable in private browsing. */ }
}

function removeStorage(key) {
    try {
        window.localStorage.removeItem(key);
    } catch (_) { /* Storage can be unavailable in private browsing. */ }
}

function notifyStateChanged() {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(STATE_EVENT));
}

// Social/messaging apps open links in their own WebView, which can't add to the home
// screen (no Share → Add to Home Screen on iOS, no install prompt on Android).
const IN_APP_BROWSER_RE = /FBAN|FBAV|FB_IAB|Instagram|LinkedInApp|Line\/|MicroMessenger|Snapchat|TikTok|musical_ly|Pinterest|Twitter|GSA\/|; wv\)/i;

export function getPwaPlatform({
    userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '',
    platform = typeof navigator !== 'undefined' ? navigator.platform : '',
    maxTouchPoints = typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0,
    userAgentDataMobile = typeof navigator !== 'undefined' ? navigator.userAgentData?.mobile : undefined,
} = {}) {
    const ipadDesktopMode = platform === 'MacIntel' && maxTouchPoints > 1;
    const ios = /iPad|iPhone|iPod/i.test(userAgent) || ipadDesktopMode;
    const android = /Android/i.test(userAgent);
    const mobile = Boolean(userAgentDataMobile) || ios || android || /Mobi/i.test(userAgent);
    const inAppBrowser = (ios || android) && IN_APP_BROWSER_RE.test(userAgent);
    let browser = 'other';
    if (/CriOS/i.test(userAgent)) browser = 'chrome-ios';
    else if (/FxiOS/i.test(userAgent)) browser = 'firefox-ios';
    else if (/EdgiOS/i.test(userAgent)) browser = 'edge-ios';
    else if (/SamsungBrowser/i.test(userAgent)) browser = 'samsung';
    else if (ios && /Safari/i.test(userAgent)) browser = 'safari';
    else if (/Edg\//i.test(userAgent)) browser = 'edge';
    else if (/Chrome|Chromium/i.test(userAgent)) browser = 'chrome';
    else if (/Safari/i.test(userAgent)) browser = 'safari-desktop';
    return { ios, android, mobile, inAppBrowser, browser };
}

/** True when CarFido is running as the installed app (launched from the home screen). */
export function isPwaStandalone() {
    if (typeof window === 'undefined') return false;
    const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches
        || window.matchMedia?.('(display-mode: fullscreen)')?.matches;
    return Boolean(standalone || window.navigator?.standalone === true);
}

/**
 * Running as the app, or the browser confirmed installation with `appinstalled`.
 * Accepting the native prompt alone is not proof: installation can still fail.
 */
export function isPwaInstalled() {
    return isPwaStandalone() || readStorage(INSTALLED_KEY) === 'true';
}

export function getPwaInstallState(now = Date.now()) {
    const platform = getPwaPlatform();
    const dismissedUntil = Number(readStorage(DISMISSED_UNTIL_KEY) || 0);
    return {
        ...platform,
        standalone: isPwaStandalone(),
        installed: isPwaInstalled(),
        engaged: readStorage(ENGAGED_KEY) === 'true',
        dismissed: dismissedUntil > now,
        canPrompt: Boolean(deferredInstallPrompt),
    };
}

/**
 * Which installation path this browser supports, from getPwaInstallState():
 *   standalone          already running as the installed app
 *   android-prompt      native install prompt available (beforeinstallprompt)
 *   android-manual      Android browser without a captured prompt: menu → Add to Home screen
 *   android-samsung     Samsung Internet's own menu wording
 *   android-in-app      in-app WebView: open in Chrome first
 *   ios-safari          Share → Add to Home Screen
 *   ios-other-browser   Chrome/Edge/Firefox on iOS 16.4+: same flow from their Share button
 *   ios-in-app          in-app WebView: open in Safari first (Apple allows no other way)
 *   desktop-prompt      desktop Chrome/Edge with the native install prompt
 *   desktop             desktop without a prompt: informational only
 */
export function getPwaInstallGuide(state) {
    if (state.standalone) return 'standalone';
    if (state.ios) {
        if (state.inAppBrowser) return 'ios-in-app';
        return ['chrome-ios', 'edge-ios', 'firefox-ios'].includes(state.browser) ? 'ios-other-browser' : 'ios-safari';
    }
    if (state.android) {
        if (state.inAppBrowser) return 'android-in-app';
        if (state.canPrompt) return 'android-prompt';
        return state.browser === 'samsung' ? 'android-samsung' : 'android-manual';
    }
    if (state.mobile) return state.canPrompt ? 'android-prompt' : 'android-manual';
    return state.canPrompt ? 'desktop-prompt' : 'desktop';
}

export function initializePwaInstall() {
    if (initialized || typeof window === 'undefined') return;
    initialized = true;

    window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault();
        deferredInstallPrompt = event;
        // Chrome only offers the prompt when the app is NOT installed, so a stale
        // "installed" flag (the user has since uninstalled) is cleared here.
        removeStorage(INSTALLED_KEY);
        notifyStateChanged();
    });

    window.addEventListener('appinstalled', () => {
        deferredInstallPrompt = null;
        writeStorage(INSTALLED_KEY, 'true');
        notifyStateChanged();
    });

    const displayMode = window.matchMedia?.('(display-mode: standalone)');
    displayMode?.addEventListener?.('change', notifyStateChanged);
}

export function markPwaEngagement(reason = 'activity') {
    if (typeof window === 'undefined') return;
    writeStorage(ENGAGED_KEY, 'true');
    try {
        window.dispatchEvent(new CustomEvent(STATE_EVENT, { detail: { reason } }));
    } catch (_) {
        notifyStateChanged();
    }
}

export function dismissPwaInstallPrompt(now = Date.now()) {
    writeStorage(DISMISSED_UNTIL_KEY, String(now + DISMISSAL_MS));
    notifyStateChanged();
}

export async function requestPwaInstall() {
    if (isPwaInstalled()) return { outcome: 'installed' };
    if (!deferredInstallPrompt) return { outcome: 'unavailable' };

    const prompt = deferredInstallPrompt;
    deferredInstallPrompt = null;
    await prompt.prompt();
    const choice = await prompt.userChoice;
    // 'accepted' means the user agreed; only the `appinstalled` event confirms success.
    const outcome = choice?.outcome === 'accepted' ? 'accepted' : 'dismissed';
    if (outcome === 'dismissed') dismissPwaInstallPrompt();
    notifyStateChanged();
    return { outcome };
}

export function subscribeToPwaInstallState(listener) {
    if (typeof window === 'undefined') return () => {};
    window.addEventListener(STATE_EVENT, listener);
    return () => window.removeEventListener(STATE_EVENT, listener);
}

export function registerCarFidoServiceWorker() {
    if (process.env.NODE_ENV !== 'production' || typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
    const register = () => {
        navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((error) => {
            console.warn('CarFido service worker registration failed:', error);
        });
    };
    if (document.readyState === 'complete') register();
    else window.addEventListener('load', register, { once: true });
}

